/**
 * Visible Chat Prices Section
 * Component for managing chat price visibility settings
 * Migrated from choco-admin-chats to admin-web with modern HeroUI components
 */

import type { CreateChatFormData } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  ChocoCheckbox,
  ChocoSelect,
} from '../../../../components/FormComponents';
import { FORM_LABELS } from '../../constants/chatOptions';

interface VisibleChatPricesSectionProps {
  /** Whether the section is disabled due to errors */
  disabled?: boolean;
  /** Custom CSS classes */
  className?: string;
}

const PRICE_VISIBILITY_OPTIONS = [
  {
    value: 'false',
    label: 'Disable prices',
    description: 'Hide prices from chat participants',
  },
  {
    value: 'true',
    label: 'Enable prices',
    description: 'Show prices to chat participants',
  },
] as const;

export const VisibleChatPricesSection: React.FC<
  VisibleChatPricesSectionProps
> = ({ disabled = false, className = '' }) => {
  const { control, watch, setValue } = useFormContext<CreateChatFormData>();

  const isPriceOverrideEnabled = watch('isPriceOverrideEnabled');

  // Create a mutable copy of the readonly array for ChocoSelect
  const selectOptions = [...PRICE_VISIBILITY_OPTIONS];

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid="VisibleChatPricesSection"
    >
      <Card>
        <CardBody className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">
              Price Visibility Override
            </h4>
            <p className="text-sm text-default-600">
              Configure whether prices should be visible in this chat
            </p>
          </div>

          {/* Price Override Enabled Checkbox */}
          <Controller
            name="isPriceOverrideEnabled"
            control={control}
            render={({ field }) => (
              <ChocoCheckbox
                id="isPriceOverrideEnabled"
                isSelected={field.value || false}
                onValueChange={(checked: boolean) => {
                  field.onChange(checked);

                  // When enabling price override, also set visible prices to true
                  if (checked) {
                    setValue('visibleChatPrices', true, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                isDisabled={disabled}
                label={FORM_LABELS.isPriceOverrideEnabled}
                description="Enabling this setting will override the default visibility of prices for this chat only."
              />
            )}
          />

          {/* Price Visibility Select */}
          <Controller
            name="visibleChatPrices"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <ChocoSelect
                  label={FORM_LABELS.visibleChatPrices}
                  placeholder="Select price visibility"
                  options={selectOptions}
                  value={
                    field.value !== undefined ? String(field.value) : undefined
                  }
                  aria-label={FORM_LABELS.visibleChatPrices}
                  onSelectionChange={(selectedValue) => {
                    if (selectedValue) {
                      field.onChange(selectedValue === 'true');
                    }
                  }}
                  isDisabled={!isPriceOverrideEnabled || disabled}
                  data-testid="visibleChatPrices"
                />

                {!isPriceOverrideEnabled && (
                  <p className="text-xs text-default-500">
                    Enable price override to configure price visibility
                  </p>
                )}
              </div>
            )}
          />
        </CardBody>
      </Card>
    </div>
  );
};
